import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faReceipt, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { eeapService, type EeapHistoryRow, type EeapSummary } from '../services/eeapService'
import { currentPayPeriod, getPayPeriodConfig, type PayPeriodConfig } from '../services/payPeriodService'
import { WEEKDAY_LABELS } from '../utils/controlCenterOperationConfig'
import { categoryLabel, formatLedgerDate, formatUsd, moneyValueClass } from './hr/advanceLedgerViews'
import { DEFAULT_ADVANCE_CATEGORY_DEFS, type AdvanceCategoryDef } from '../utils/advanceCategories'
import { AdvanceDescription, DateSortChip, LockedBalanceBadge } from './AdvanceTableCells'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'
import {
  hasEstablishedBalance,
  sortAdvanceRows,
  withCombinedRunningBalances,
  type DateSort,
} from './profileAdvanceBalances'

function MoneyTd({ value, positiveGreen }: { value: number; positiveGreen?: boolean }) {
  return (
    <td className={moneyValueClass(value, { positiveGreen })}>
      {formatUsd(value)}
    </td>
  )
}

function HistoryRow({ row, catalog }: { row: EeapHistoryRow; catalog: AdvanceCategoryDef[] }) {
  const { openDocument } = useDocumentViewer()
  const photos = Array.isArray(row.imageUrls) ? row.imageUrls.filter(Boolean) : []
  return (
    <tr>
      <td>{formatLedgerDate(row.date)}</td>
      <td>{row.categoryLabel || categoryLabel(row.category, catalog)}</td>
      <td>{row.itemCategory || '—'}</td>
      <td>
        <AdvanceDescription
          description={row.description}
          scheduled={row.scheduled}
          photoCount={photos.length}
          onViewPhotos={photos.length ? () => {
            const title = photos.length > 1 ? `Receipt (1 of ${photos.length})` : 'Receipt'
            openDocument(title, 'Image', photos[0])
          } : undefined}
        />
      </td>
      <MoneyTd value={Number(row.amount || 0)} />
      <MoneyTd value={Number(row.employerContribution || 0)} positiveGreen />
      <MoneyTd value={Number(row.employeeAmount || 0)} />
      <td className={`font-medium ${moneyValueClass(row.runningBalance) || ''}`}>
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {formatUsd(row.runningBalance)}
          {hasEstablishedBalance(row) ? <LockedBalanceBadge /> : null}
        </span>
      </td>
    </tr>
  )
}

const ProfileAdvancesTab: React.FC = () => {
  const [summary, setSummary] = useState<EeapSummary | null>(null)
  const [history, setHistory] = useState<EeapHistoryRow[]>([])
  const [payPeriod, setPayPeriod] = useState<PayPeriodConfig | null>(null)
  const [catalog, setCatalog] = useState<AdvanceCategoryDef[]>([...DEFAULT_ADVANCE_CATEGORY_DEFS])
  const [dateSort, setDateSort] = useState<DateSort>('asc')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [nextSummary, rows, pay, nextCatalog] = await Promise.all([
          eeapService.getSummary(),
          eeapService.listHistory(),
          getPayPeriodConfig(),
          eeapService.listAdvanceCategories().catch(() => [...DEFAULT_ADVANCE_CATEGORY_DEFS]),
        ])
        if (cancelled) return
        setSummary(nextSummary)
        setHistory(rows)
        setPayPeriod(pay)
        if (nextCatalog.length) setCatalog(nextCatalog)
      } catch (e) {
        if (cancelled) return
        setSummary(null)
        setHistory([])
        setError(e instanceof Error ? e.message : 'Failed to load advances')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const period = useMemo(() => (payPeriod ? currentPayPeriod(payPeriod) : null), [payPeriod])
  const periodLabel = useMemo(() => {
    if (!period) return ''
    const start = period.startDate.toLocaleDateString()
    const end = period.endDate.toLocaleDateString()
    return `${start} – ${end}`
  }, [period])
  const nextPayDateLabel = period?.nextPayDate
    ? period.nextPayDate.toLocaleDateString()
    : ''
  const weekdayName = payPeriod
    ? WEEKDAY_LABELS[payPeriod.payDateWeekday] || 'Friday'
    : 'Friday'
  const allowance = summary?.annualAllowance ?? 200
  const remaining = summary?.remainingAllowance ?? 0
  const resetDateLabel = useMemo(() => {
    if (!summary?.windowResetsOn) return ''
    try {
      return new Date(summary.windowResetsOn).toLocaleDateString()
    } catch {
      return ''
    }
  }, [summary?.windowResetsOn])
  const outstanding = summary?.outstandingBalance ?? 0
  const outstandingAdvance = summary?.outstandingAdvance ?? 0
  const outstandingEeapAdvance = summary?.outstandingEeapAdvance ?? 0
  const totalOutstanding = summary?.outstandingTotal
    ?? (outstanding + outstandingAdvance + outstandingEeapAdvance)
  const tableRows = useMemo(() => {
    const upcoming = summary?.upcomingDeductions ?? []
    const balanced = withCombinedRunningBalances(history, upcoming, totalOutstanding)
    return sortAdvanceRows(balanced, dateSort)
  }, [history, summary, totalOutstanding, dateSort])
  const hasRows = tableRows.length > 0

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faReceipt} />
          Advances
        </h2>
        <p className="text-sm text-base-content/70">
          Each category follows the deduction method HR assigned. EEAP-style categories take $60
          each payday until that balance is $0, and deduct the amount over $350 on the next
          paycheck. Next-payday categories are taken in full on the next paycheck. First pay
          period of the month takes the full balance on that period&apos;s payday. Use Request
          EEAP above to submit a new request.
        </p>

        {error && <div className="alert alert-error mt-3"><span>{error}</span></div>}

        {loading ? (
          <div className="flex justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} className="text-2xl text-primary animate-spin" />
          </div>
        ) : (
          <>
            <div className="stats stats-vertical sm:stats-horizontal shadow bg-base-200 my-4 w-full overflow-x-hidden">
              <div className="stat min-w-0">
                <div className="stat-title">Current allowance</div>
                <div className="stat-value text-primary">{formatUsd(remaining)}</div>
                <div className="stat-desc">
                  of {formatUsd(allowance)} • resets {resetDateLabel ? `on ${resetDateLabel}` : 'on hire anniversary'}
                </div>
              </div>
              <div className="stat min-w-0">
                <div className="stat-title">Outstanding balance</div>
                <div className={`stat-value ${totalOutstanding > 0 ? 'text-warning' : ''}`}>{formatUsd(totalOutstanding)}</div>
                <div className="stat-desc">EEAP and advances combined</div>
              </div>
              <div className="stat min-w-0">
                <div className="stat-title">Current pay period</div>
                <div className="stat-value text-lg sm:text-2xl">{periodLabel || '—'}</div>
                <div className="stat-desc">
                  {payPeriod?.payPeriodType === 'biweekly' ? 'Biweekly' : 'Weekly'}
                  {` • paid following ${weekdayName}`}
                  {nextPayDateLabel ? ` • next payday ${nextPayDateLabel}` : ''}
                </div>
              </div>
            </div>

            {!hasRows ? (
              <div className="text-sm text-base-content/60">No transactions recorded yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>
                        <span className="inline-flex items-center gap-2">
                          Date
                          <DateSortChip value={dateSort} onChange={setDateSort} />
                        </span>
                      </th>
                      <th>Category</th>
                      <th>Item</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Employer</th>
                      <th>Employee</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => (
                      <HistoryRow key={row.id} row={row} catalog={catalog} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ProfileAdvancesTab
